/**
 * customMotions.ts
 * ユーザーが作成したカスタムモーション（ジェスチャー）の一覧を localStorage で管理する
 */
import { useSyncExternalStore } from 'react';
import {
  DEFAULT_FPS,
  createStandbyTimeline,
  type MoveTrackId,
  type TimelineData,
} from '../animation/animationUtils';
import type { SoundEffect } from './soundEffects';
import { isSoundEffect } from './soundEffects';

export type CustomMotionPose = {
  timeMs: number;
  joints: Record<string, number>;
  movement?: Partial<Record<MoveTrackId, number>>;
  ledColor?: string;
  sound?: SoundEffect;
};

export type CustomMotionConfig = {
  id: string;
  label: string;
  poses: CustomMotionPose[];
  createdAt?: string;
  updatedAt?: string;
};

const STORAGE_KEY = 'filo:custom-motions:v1';

const normalizePoses = (value: unknown): CustomMotionPose[] => {
  if (!Array.isArray(value)) return [];
  return value
    .filter((pose): pose is CustomMotionPose =>
      !!pose
      && typeof pose === 'object'
      && Number.isFinite(Number((pose as CustomMotionPose).timeMs))
      && typeof (pose as CustomMotionPose).joints === 'object')
    .map((pose) => ({
      timeMs: Math.max(0, Math.round(Number(pose.timeMs))),
      joints: Object.fromEntries(
        Object.entries(pose.joints).filter(([, value]) => Number.isFinite(Number(value))),
      ),
      movement: pose.movement && typeof pose.movement === 'object'
        ? Object.fromEntries(
          Object.entries(pose.movement).filter(([, value]) => Number.isFinite(Number(value))),
        ) as Partial<Record<MoveTrackId, number>>
        : undefined,
      ledColor: typeof pose.ledColor === 'string' ? pose.ledColor : undefined,
      sound: typeof pose.sound === 'string' ? pose.sound as SoundEffect : undefined,
    }))
    .sort((first, second) => first.timeMs - second.timeMs);
};

const normalize = (value: unknown): CustomMotionConfig[] => {
  if (!Array.isArray(value)) return [];
  const migrationTime = Date.now();
  return value
    .filter((item): item is CustomMotionConfig =>
      !!item
      && typeof item === 'object'
      && typeof (item as CustomMotionConfig).id === 'string'
      && typeof (item as CustomMotionConfig).label === 'string')
    .map((item, index, items) => {
      const fallbackDate = new Date(migrationTime - (items.length - index) * 1000).toISOString();
      const createdAt = typeof item.createdAt === 'string' ? item.createdAt : fallbackDate;
      return {
        id: item.id,
        label: item.label,
        poses: normalizePoses((item as CustomMotionConfig).poses),
        createdAt,
        updatedAt: typeof item.updatedAt === 'string' ? item.updatedAt : createdAt,
      };
    })
    .sort((first, second) => (second.updatedAt ?? '').localeCompare(first.updatedAt ?? ''));
};

const load = (): CustomMotionConfig[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? normalize(JSON.parse(raw)) : [];
  } catch {
    return [];
  }
};

let snapshot: CustomMotionConfig[] = load();
const listeners = new Set<() => void>();

const publish = (next: CustomMotionConfig[]) => {
  snapshot = [...next].sort((first, second) => (second.updatedAt ?? '').localeCompare(first.updatedAt ?? ''));
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
  } catch {
    // 容量超過などは保存を諦める（画面上の一覧は更新する）
  }
  listeners.forEach((listener) => listener());
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const getCustomMotions = (): CustomMotionConfig[] => snapshot;

export const addCustomMotion = (motion: CustomMotionConfig): void => {
  const existing = snapshot.find((item) => item.id === motion.id);
  const savedAt = new Date().toISOString();
  publish([
    {
      ...motion,
      createdAt: existing?.createdAt ?? motion.createdAt ?? savedAt,
      updatedAt: savedAt,
    },
    ...snapshot.filter((item) => item.id !== motion.id),
  ]);
};

export const removeCustomMotion = (id: string): void => {
  publish(snapshot.filter((item) => item.id !== id));
};

export const getCustomMotionLabel = (id?: string) =>
  snapshot.find((motion) => motion.id === id)?.label;

export const getCustomMotion = (id?: string) =>
  snapshot.find((motion) => motion.id === id);

export const customMotionToTimeline = (motion: CustomMotionConfig): TimelineData => {
  const timeline = createStandbyTimeline();
  const poses = [...motion.poses].sort((first, second) => first.timeMs - second.timeMs);
  const lastTimeMs = poses.length > 0 ? poses[poses.length - 1].timeMs : 0;
  const totalFrames = Math.max(1, Math.round((lastTimeMs / 1000) * DEFAULT_FPS));
  const movementKeyframes = new Map<MoveTrackId, Array<{ frame: number; value: number }>>();
  timeline.movement.forEach((track) => movementKeyframes.set(track.id, []));
  poses.forEach((pose) => {
    if (!pose.movement || typeof pose.movement !== 'object') return;
    Object.entries(pose.movement).forEach(([trackId, value]) => {
      const validTrack = trackId as MoveTrackId;
      if (!movementKeyframes.has(validTrack) || !Number.isFinite(Number(value))) return;
      movementKeyframes.get(validTrack)!.push({
        frame: Math.round((pose.timeMs / 1000) * DEFAULT_FPS),
        value: Number(value),
      });
    });
  });
  return {
    ...timeline,
    name: motion.label,
    totalFrames,
    joints: timeline.joints.map((joint) => ({
      ...joint,
      keyframes: poses
        .filter((pose) => Number.isFinite(pose.joints[joint.id]))
        .map((pose) => ({
          frame: Math.round((pose.timeMs / 1000) * DEFAULT_FPS),
          value: pose.joints[joint.id],
        })),
    })),
    movement: timeline.movement.map((track) => ({
      ...track,
      keyframes: movementKeyframes.get(track.id) ?? track.keyframes,
    })),
    eyeLedKeyframes: poses
      .filter((pose) => typeof pose.ledColor === 'string')
      .map((pose) => ({
        frame: Math.round((pose.timeMs / 1000) * DEFAULT_FPS),
        color: pose.ledColor!,
      })),
    soundKeyframes: poses
      .filter((pose) => isSoundEffect(pose.sound))
      .map((pose) => ({
        frame: Math.round((pose.timeMs / 1000) * DEFAULT_FPS),
        sound: pose.sound!,
      })),
  };
};

/** カスタムモーション一覧を購読する（作成画面での追加・削除が即座に反映される） */
export const useCustomMotions = (): CustomMotionConfig[] =>
  useSyncExternalStore(subscribe, getCustomMotions, getCustomMotions);
